import axios from 'axios';

import { Flex, Button } from '@chakra-ui/react';

import { createIntl, createIntlCache } from 'react-intl';
import en from '../lang/en.json';

import ErrorAccordion from '../components/utils/ErrorAccordion';
import { getDBConstraintTableAndColumns } from './globalConstraints';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { toast } from 'utils/toast';

const cache = createIntlCache();
const intl = createIntl({ locale: 'en', messages: en }, cache);

export const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
        authority: process.env.REACT_APP_AUTH_AUTHORITY,
        redirectUri: process.env.REACT_APP_AUTH_REDIRECT_URI,
        postLogoutRedirectUri: process.env.REACT_APP_AUTH_REDIRECT_URI,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
        secureCookies: false,
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(() => {
    // Default to using the first account if no account is active on page load
    if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
        // Account selection logic is app dependent. Adjust as needed for different use cases.
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    // Optional - This will update account state if a user signs in from another tab or window
    // msalInstance.enableAccountStorageEvents();

    msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
            const account = event.payload.account;
            msalInstance.setActiveAccount(account);
        }
    });
});

export const scopes = [process.env.REACT_APP_AUTH_SCOPE];

const renderToast = (errMsg, errDetails) => {
    const continueLabel = intl.formatMessage({ id: 'common_continue' });

    const status = 'error';
    // toast light and dark colors per status are defined in src\theme\colors.js
    const textColor = `${status}-toast-text-color`;
    const bgColor = `${status}-toast-background`;
    const borderColor = `${status}-toast-border-color`;

    if (!toast?.isActive('noPermissionsToast')) {
        toast({
            render: ({ onClose }) => (
                <Flex
                    wrap="wrap"
                    direction="row"
                    align="center"
                    px={6}
                    py={4}
                    borderWidth="1px"
                    borderRadius="md"
                    color={textColor}
                    bg={bgColor}
                    borderColor={borderColor}
                    minW={750}
                >
                    <ErrorAccordion status={status} message={errMsg} details={errDetails} />
                    <Button textTransform="capitalize" ml="auto" variant="secondary" bg="white" onClick={onClose}>
                        {continueLabel}
                    </Button>
                </Flex>
            ),
            position: 'top',
            id: 'noPermissionsToast',
            duration: 20000,
        });
    }
};

export const setupInterceptors = (navigate) => {
    const account = msalInstance.getActiveAccount();

    const reqId = axios.interceptors.request.use(async (config) => {
        try {
            if (config.headers?.powerBiAuthorization) {
                config.headers.Authorization = config.headers.powerBiAuthorization;
                delete config.headers.powerBiAuthorization;
            } else {
                const { accessToken } = await msalInstance.acquireTokenSilent({ scopes, account });
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
        } catch (err) {
            // something went wrong with the silent token acquisition
            console.dir(err);
            msalInstance.loginRedirect();
        }

        return config;
    });

    const getDBConstraintTableAndColumnsInfo = async ({ constraintName }) => {
        let extraInfo = '';
        if (!constraintName) return extraInfo;

        try {
            const response = await getDBConstraintTableAndColumns({ constraintName });

            if (response) {
                extraInfo =
                    'Table: [' +
                    response.tableName +
                    '] Column(s): [' +
                    response.columns +
                    '] Constraint: [' +
                    constraintName +
                    ']  ';
            }
        } catch {
            // Silent catch - we don't want to fail the main operation if this extra info fetch fails
        }
        return extraInfo;
    };

    const resId = axios.interceptors.response.use(
        (response) => {
            // Any status code that lie within the range of 2xx cause this function to trigger
            return response;
        },
        async (error) => {
            let data;
            if (error.response) {
                data = error.response.data;
                const isPowerBiError = data?.error?.code?.startsWith('PowerBI');

                if (data instanceof Blob) {
                    data = JSON.parse(await data.text());
                }

                /*
                API Exception Middleware populates the following object structure:
                {
                    int code 
                    string error 
                    string details 
                    string instance 
                    string errorType 
                    string objectName
                    enum errorMessageType 
                    {
                        General = 0,
                        OnDeleteChildrenFound = 1,
                        UniqueConstraintError = 2,
                        DBNetworkError = 3,
                        UniqueItemTriggerError = 4,
                        CheckConstraintViolation = 5,
                        IntegrityConstraintViolation = 6
                    }
                }
                */

                if (data.errorType === 'NO_MAIN_PERMISSIONS') {
                    navigate('/connection', { replace: true });
                }

                // Any status codes that falls outside the range of 2xx cause this function to trigger
                if (error.response.status === axios.HttpStatusCode.Unauthorized && !isPowerBiError) {
                    // Status 401 Unauthorized
                    renderToast(data.error);
                } else if (
                    data &&
                    !error.config.suppressErrorMessageHandler &&
                    !data.issues &&
                    !data.validationResult?.issues
                ) {
                    let errMsg = '';
                    const errTitle = data.title ?? '';
                    const dataError = data.errors ? JSON.stringify(data.errors) : data.error;
                    const dataDetails = data.details ?? dataError;

                    //Getting more information about the error
                    let errDetails = errTitle + dataDetails;
                    let constraintName = data.objectName ?? '';

                    const extraConstraintInfo = await getDBConstraintTableAndColumnsInfo({ constraintName });

                    if (extraConstraintInfo) {
                        constraintName = extraConstraintInfo;
                        errDetails = extraConstraintInfo + ' ' + errDetails;
                    }

                    switch (data.errorMessageType) {
                        case 1: //OnDeleteChildrenFound
                            errMsg = intl.formatMessage(
                                { id: 'common_delete_linked_children_error' },
                                { constraintName }
                            );
                            renderToast(errMsg, errDetails ?? '');
                            break;
                        case 2: //UniqueConstraintError
                            errMsg = intl.formatMessage({ id: 'common_unique_constraint_error' }, { constraintName });
                            renderToast(errMsg, errDetails ?? '');
                            break;
                        case 3: //DBNetworkError
                            renderToast(intl.formatMessage({ id: 'common_db_network_error' }), errDetails ?? '');
                            navigate('/connection', { replace: true });
                            break;
                        case 4: //UniqueItemTriggerError
                            const url = error.response.config?.url ?? '';
                            let duplicateErrMsg = intl.formatMessage({ id: 'common_unique_item_name_update_error' });
                            if (url.toLowerCase().includes('create')) {
                                duplicateErrMsg = intl.formatMessage({ id: 'common_unique_item_name_create_error' });
                            }
                            renderToast(duplicateErrMsg);
                            break;
                        case 5: //CheckConstraintViolation
                            errMsg = intl.formatMessage(
                                { id: 'common_check_constraint_violation_error' },
                                { constraintName }
                            );
                            renderToast(errMsg, errDetails ?? '');
                            break;
                        case 6: //IntegrityConstraintViolation
                            errMsg = intl.formatMessage(
                                { id: 'common_integrity_constraint_violation_error' },
                                { constraintName }
                            );
                            renderToast(errMsg, errDetails ?? '');
                            break;
                        default:
                            errMsg = intl.formatMessage({ id: 'common_generic_processing_error' });
                            renderToast(errMsg, errDetails ?? '');
                            break;
                    }
                }
            }
            return Promise.reject(error);
        }
    );

    return { reqId, resId };
};

export const clearInterceptors = (reqId, resId) => {
    axios.interceptors.request.eject(reqId);
    axios.interceptors.response.eject(resId);
};
